/**
* @ngdoc    object
* @module   csm.books.data
* @name     csm.books.data.InventoryProduct.type:InventoryProduct
* @description
* Provides a function to generate an InventoryProduct object. If a source object
* has been provided, values will be copied over.
**/

/* @ngInject */
export function inventoryProduct(Tools) {
  function InventoryProduct(src) {
    /***************************************************************************
     * Properties
     ***************************************************************************/
    /** Self Reference. */ var mThis = this;

    /** ID. */ mThis.id = 0;
    /** Active flag. */ mThis.active = true;
    /** List of InventoryProductOptions. */ mThis.options = [];
    /** Cost From Inventory Flag. */ mThis.costFromInventory = false;
    /** Total quantity purchased. */ mThis.costQuantity = 0;
    /** Total Inventory Value. */ mThis.inventoryValue = 0;
    /** Preset Cost. */ mThis.presetCost = 0;
    /** Total inventory Cost. */ mThis.totalCost = 0;

    /*--------------*/
    /* Input values */
    /*--------------*/
    /** New Cost. */ mThis.newCost = null;
    /** New Encumbered Quantity. */ mThis.newEncumbered = null;
    /** New Quantity on hand. */ mThis.newQty = null;

    /*-----------*/
    /* Read Only */
    /*-----------*/
    /** Average Cost. */ mThis.averageCost = 0;
    /** Count. */ mThis.count = 0; // Quantity Available
    /** Encumbered Quantity. */ mThis.encumbered = 0; // Quantity Reserved (negative value, decreased when ordered)

    /*-------------------------------------*/
    /* Copy source properties if available */
    /*-------------------------------------*/
    if (src) angular.extend(mThis, src);

    /** Quantity On Hand. */
    Object.defineProperty(mThis, 'actualQty', {
      get: function () {
        return getNumber(mThis.count) - getNumber(mThis.encumbered);
      }
    });

    /** Change in Quantity Reserved. */
    Object.defineProperty(mThis, 'changeInEncumberance', {
      get: function () {
        return mThis.newEncumbered != null
          ? getNumber(mThis.newEncumbered) - getNumber(mThis.encumbered)
          : 0;
      }
    });

    /** Change in Quantity On Hand. */
    Object.defineProperty(mThis, 'changeInQty', {
      get: function () {
        return mThis.newQty != null
          ? getNumber(mThis.newQty) - getNumber(mThis.actualQty)
          : 0;
      }
    });

    /** Change in Value. */
    Object.defineProperty(mThis, 'changeInValue', {
      get: function () {
        var oldCost = !mThis.id
          ? 0
          : mThis.costFromInventory
          ? mThis.averageCost
          : mThis.presetCost;
        var oldQty = getNumber(mThis.count);
        var oldValue = oldCost * oldQty;

        var currCost = Tools.coalesce(mThis.newCost, oldCost);
        var currQty = Tools.coalesce(mThis.newQty, mThis.count);
        var currValue = currCost * currQty;

        return Tools.roundMoney(currValue - oldValue);
      }
    });

    /** Returns <i>true</i> when this item includes an adjustment. */
    Object.defineProperty(mThis, 'hasAdjustment', {
      get: function () {
        return !(
          mThis.changeInQty == 0 &&
          mThis.changeInValue == 0 &&
          mThis.changeInEncumberance == 0
        );
      }
    });

    /***************************************************************************
     * Property: cost */
    /**
     * New item cost. Getter returns the new cost or the current average cost.
     * Input is ignored if no new quantity. Input of <code>NaN</code> or
     * <code>0</code> clears all new values. Valid input sets new cost and
     * recalculates new value using new quantity.
     ***************************************************************************/
    Object.defineProperty(mThis, 'cost', {
      get: function () {
        return getNumber(
          mThis.newCost != null
            ? mThis.newCost
            : mThis.costFromInventory
            ? mThis.averageCost != 0
              ? mThis.averageCost
              : NaN
            : mThis.presetCost
        );
      },
      set: function (c) {
        /*-----------------------------------------------------*/
        /* Clear new values if input is 0 or NaN. Otherwise,   */
        /* set the new average cost and recalculate new value. */
        /*-----------------------------------------------------*/
        if (isNaN(c)) mThis.qty = NaN;
        else mThis.newCost = c;

        if (!(mThis.costFromInventory || mThis.count == 0))
          mThis.presetCost = c;
      }
    });

    /*-----------------------------------------------------------*/
    /* This property is needed for bound input controls to       */
    /* ensure the getter doesn't change the user's input.        */
    /*                                                           */
    /* EX: User types a '.', the numberInput converts that to a  */
    /* NaN for the cost setter. The cost getter then returns a 0 */
    /*-----------------------------------------------------------*/
    var mCostInput;
    Object.defineProperty(mThis, 'costInput', {
      get: function () {
        return mCostInput != undefined ? mCostInput : mThis.cost;
      },
      set: function (c) {
        /*----------------------------------------*/
        /* Clear the input if it matches the base */
        /*----------------------------------------*/
        if (c == mThis.cost) mCostInput = undefined;
        /*-----------------------------*/
        /* Otherwise update the values */
        /*-----------------------------*/ else {
          mCostInput = c;
          mThis.cost = c;
        }
      }
    });

    /***************************************************************************
     * Property: encumberance */
    /**
     * New encumbered quantity. Updates new encumbered value. Make sure it is a
     * negative value when saved, and positive when displayed.
     ***************************************************************************/
    Object.defineProperty(mThis, 'encumberance', {
      get: function () {
        return -Number(
          Tools.coalesce(mThis.newEncumbered, mThis.encumbered, 0)
        );
      },
      set: function (e) {
        mThis.newEncumbered = -e;
      }
    });

    /*-----------------------------------------------------------*/
    /* This property is needed for bound input controls to       */
    /* ensure the getter doesn't change the user's input.        */
    /*-----------------------------------------------------------*/
    var mEncumberanceInput;
    Object.defineProperty(mThis, 'encumberanceInput', {
      get: function () {
        return mEncumberanceInput != undefined
          ? mEncumberanceInput
          : mThis.encumberance;
      },
      set: function (e) {
        /*----------------------------------------*/
        /* Clear the input if it matches the base */
        /*----------------------------------------*/
        if (e == mThis.encumberance) mEncumberanceInput = undefined;
        /*-----------------------------*/
        /* Otherwise update the values */
        /*-----------------------------*/ else {
          mEncumberanceInput = e;
          mThis.encumberance = e;
        }
      }
    });

    /***************************************************************************
     * Property: qty */
    /**
     * New quantity on hand. Updates new inventory value. Make sure to account
     * for the encumbered amount.
     ***************************************************************************/
    Object.defineProperty(mThis, 'qty', {
      get: function () {
        return Tools.coalesce(mThis.newQty, mThis.actualQty, 0);
      },
      set: function (q) {
        /*------------------------------------------------------*/
        /* If there is no cost data in the Inventory table, the */
        /* new quantity must be greater than the old.  This is  */
        /* the only way to set the cost in the Inventory table. */
        /*------------------------------------------------------*/
        if (q < mThis.actualQty && !mThis.costFromInventory) q = NaN;

        /*--------------------------*/
        /* Clear new values if NaN. */
        /*--------------------------*/
        if (isNaN(q)) {
          mThis.newQty = NaN;
          mThis.newCost = NaN;
        } else if (mThis.newQty == null || q != mThis.newQty)
          /*-----------------------------*/
          /* Set new quantity and value. */
          /*-----------------------------*/
          mThis.newQty = q;

        /*------------------------------------------------*/
        /* Set actual adjustment quantity.  Use current   */
        /* quantity if no new quantity so new quantity is */
        /* not zero when adjustment is sent to service.   */
        /*------------------------------------------------*/
        if (mThis.newQty == null) mThis.newQty = mThis.actualQty;
      }
    });

    /*-----------------------------------------------------------*/
    /* This property is needed for bound input controls to       */
    /* ensure the getter doesn't change the user's input.        */
    /*-----------------------------------------------------------*/
    var mQtyInput;
    Object.defineProperty(mThis, 'qtyInput', {
      get: function () {
        return mQtyInput != undefined ? mQtyInput : mThis.qty;
      },
      set: function (q) {
        /*----------------------------------------*/
        /* Clear the input if it matches the base */
        /*----------------------------------------*/
        if (q == mThis.qty) mQtyInput = undefined;
        /*-----------------------------*/
        /* Otherwise update the values */
        /*-----------------------------*/ else {
          mQtyInput = q;
          mThis.qty = q;
        }
      }
    });

    /***************************************************************************
     * addOption */
    /**
     * Add the supplied option to the list of options on this instance. If one
     * already exists, that will be replaced with the passed option as it will
     * be more up to date.
     ***************************************************************************/
    mThis.addOption = function (option) {
      option.productID = mThis.id;

      /*--------------------------------------*/
      /* Look for a matching masterID, and    */
      /* remove it so we can add the new one. */
      /*--------------------------------------*/
      for (var i = 0; i < mThis.options.length; i++) {
        if (mThis.options[i].masterOptionID == option.masterOptionID) {
          mThis.options.splice(i, 1);
          break;
        }
      }
      mThis.options.push(option);
    };

    /***************************************************************************
     * clone */
    /**
     * Returns a deep copy of this instance.
     ***************************************************************************/
    mThis.clone = function () {
      return new InventoryProduct(angular.copy(mThis));
    };

    /***************************************************************************
     * equals */
    /**
     * Returns an indication if the passed inventory product refers to the same
     * options.
     ***************************************************************************/
    mThis.equals = function (product) {
      /*---------------------------------------------------------------*/
      /* The options can't be the same if one has more than the other. */
      /*---------------------------------------------------------------*/
      if (mThis.options.length != product.options.length) return false;

      /*---------------------------------------*/
      /* Find the corresponding option in      */
      /* this class against the passed product */
      /*---------------------------------------*/
      var foundAll = true;

      for (var o1 = 0; o1 < mThis.options.length; o1++) {
        var found = product.options.some(function (po) {
          return (
            mThis.options[o1].masterOptionID == po.masterOptionID &&
            mThis.options[o1].itemOptionID == po.itemOptionID
          );
        });

        foundAll = foundAll && found;

        if (!foundAll) break;
      }

      /*-----------------------------------------------------------------------*/
      /* If we reach this point, we either haven't found a match, or there     */
      /* were no options for either product. This would be because the         */
      /* product is the default product, so they should both have the same id. */
      /*-----------------------------------------------------------------------*/
      return foundAll || product.id == mThis.id;
    };

    /** Returns numeric value for arithmetic. */
    function getNumber(val) {
      return Number(val) || 0;
    }
  }

  return InventoryProduct;
}
